import { inject, Injectable, NgZone, signal } from '@angular/core';
import bezier from 'bezier-easing';

export interface SpinConfig {
  targetRoll: number;
  won: boolean; // Backend result - source of truth
  isFastMode: boolean;
}

export interface SpinResult {
  won: boolean;
  roll: number;
}

@Injectable()
export class HexagonSpinService {
  // Animation state signals (exposed for component binding)
  readonly currentRotation = signal<number>(0);
  readonly displayedRoll = signal<number | null>(null);
  readonly isSpinning = signal<boolean>(false);
  readonly trailStartAngle = signal<number>(0);
  readonly trailWidth = signal<number>(0);

  private readonly ngZone = inject(NgZone);
  private animationId: number | null = null;
  private onCompleteCallback: ((result: SpinResult) => void) | null = null;

  // Animation constants
  private readonly MAX_TRAIL_WIDTH = 60; // degrees
  private readonly NORMAL_DURATION = 8000; // ms (6s spin + 2s slow finish)
  private readonly FAST_DURATION = 2800; // ms (2s spin + 0.8s slow finish)
  private readonly NORMAL_ROTATIONS = 3;
  private readonly FAST_ROTATIONS = 1;
  private readonly TRAIL_CATCHUP_NORMAL = 500; // ms
  private readonly TRAIL_CATCHUP_FAST = 200; // ms

  // Bezier easing - fast start, long visible slide at the end to bait players
  private readonly easing = bezier(0.15, 0.8, 0.3, 1);

  constructor() {}

  /**
   * Start the spin animation
   * @param config Spin configuration (includes backend won result)
   * @param onComplete Callback when animation completes
   */
  spin(config: SpinConfig, onComplete: (result: SpinResult) => void): void {
    this.cancel();
    this.onCompleteCallback = onComplete;
    this.runSpinAnimation(config);
  }

  /**
   * Cancel any running animation
   */
  cancel(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Reset animation state
   */
  reset(): void {
    this.cancel();
    this.currentRotation.set(0);
    this.displayedRoll.set(null);
    this.isSpinning.set(false);
    this.trailStartAngle.set(0);
    this.trailWidth.set(0);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.cancel();
    this.onCompleteCallback = null;
  }

  private runSpinAnimation(config: SpinConfig): void {
    const { targetRoll, won, isFastMode } = config;

    // Reset previous state
    this.displayedRoll.set(null);
    this.currentRotation.set(0);
    this.isSpinning.set(true);
    this.trailWidth.set(0);

    // Calculate animation parameters
    const targetDegrees = (targetRoll / 100) * 360;
    const fullRotations = isFastMode ? this.FAST_ROTATIONS : this.NORMAL_ROTATIONS;
    const totalRotation = 360 * fullRotations + targetDegrees;
    const duration = isFastMode ? this.FAST_DURATION : this.NORMAL_DURATION;

    const startTime = performance.now();
    let lastTrailWidth = 0;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = this.easing(progress);

      const arrowDeg = easedProgress * totalRotation;
      const currentTrailWidth = Math.min(arrowDeg, this.MAX_TRAIL_WIDTH);
      const trailStart = (arrowDeg - currentTrailWidth) % 360;

      lastTrailWidth = currentTrailWidth;

      this.currentRotation.set(arrowDeg % 360);
      this.trailStartAngle.set(trailStart);
      this.trailWidth.set(currentTrailWidth);

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.ngZone.run(() => {
          this.runTrailCatchUp({
            finalArrowDeg: targetDegrees,
            startTrailWidth: lastTrailWidth,
            targetRoll,
            won,
            isFastMode,
          });
        });
      }
    };

    // Run animation outside Angular zone to avoid change detection per frame
    this.ngZone.runOutsideAngular(() => {
      this.animationId = requestAnimationFrame(animate);
    });
  }

  private runTrailCatchUp(params: {
    finalArrowDeg: number;
    startTrailWidth: number;
    targetRoll: number;
    won: boolean;
    isFastMode: boolean;
  }): void {
    const { finalArrowDeg, startTrailWidth, targetRoll, won, isFastMode } = params;
    const catchUpDuration = isFastMode ? this.TRAIL_CATCHUP_FAST : this.TRAIL_CATCHUP_NORMAL;
    const startTime = performance.now();

    const animateCatchUp = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / catchUpDuration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic

      const currentTrailWidth = startTrailWidth * (1 - easedProgress);
      const trailStart = (finalArrowDeg - currentTrailWidth + 360) % 360;

      this.trailStartAngle.set(trailStart);
      this.trailWidth.set(currentTrailWidth);

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animateCatchUp);
      } else {
        this.completeAnimation(targetRoll, finalArrowDeg, won);
      }
    };

    this.animationId = requestAnimationFrame(animateCatchUp);
  }

  private completeAnimation(targetRoll: number, finalArrowDeg: number, won: boolean): void {
    this.currentRotation.set(finalArrowDeg);
    this.displayedRoll.set(targetRoll);
    this.isSpinning.set(false);
    this.trailWidth.set(0);

    // Use the backend's won value directly - no recalculation!
    if (this.onCompleteCallback) {
      this.onCompleteCallback({ won, roll: targetRoll });
    }
  }
}
